import { Controller } from "@hotwired/stimulus"
import { createPopper } from "@popperjs/core"

// Generic AI text assist controller — works with any backend that returns
// { success: true, suggestion: "..." } from a POST endpoint.
//
// Host app must:
//   1. Pin @popperjs/core in its importmap
//   2. Register this controller: application.register("dlnk-ai-text-assist", ...)
//   3. Provide a POST endpoint (configured via data-dlnk-ai-text-assist-url-value)
//
export default class extends Controller {
  static targets = ["input", "trigger", "menu", "suggestion", "suggestionText", "loading", "actions", "status"]
  static values = {
    url: { type: String, default: "/api/ai_text_assists" },
    editorType: { type: String, default: "auto" },
    open: { type: Boolean, default: false },
    maxLength: { type: Number, default: 0 },
    undoMessage: { type: String, default: "Text modified" },
    undoAction: { type: String, default: "Undo" }
  }

  connect() {
    this.popperInstance = null
    this.currentSuggestion = null
    this.previousValue = null
    this.detectEditorType()
    this.bindKeyboardShortcuts()
  }

  disconnect() {
    this.destroyPopper()
    this.unbindKeyboardShortcuts()
  }

  detectEditorType() {
    if (this.editorTypeValue !== "auto") {
      this.editor = this.findEditor()
      return
    }

    const lexxy = this.element.querySelector("lexxy-editor")
    if (lexxy) {
      this.editorTypeValue = "lexxy"
      this.editor = lexxy
      return
    }

    const textarea = this.element.querySelector("textarea")
    if (textarea) {
      this.editorTypeValue = "textarea"
      this.editor = textarea
      return
    }

    const input = this.element.querySelector("input[type='text'], input:not([type])")
    if (input) {
      this.editorTypeValue = "input"
      this.editor = input
    }
  }

  findEditor() {
    switch (this.editorTypeValue) {
      case "lexxy":
        return this.element.querySelector("lexxy-editor")
      case "textarea":
        return this.element.querySelector("textarea")
      case "input":
        return this.element.querySelector("input[type='text'], input:not([type])")
      default:
        return null
    }
  }

  bindKeyboardShortcuts() {
    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.handleKeydown)
  }

  unbindKeyboardShortcuts() {
    document.removeEventListener("keydown", this.handleKeydown)
  }

  handleKeydown(event) {
    if ((event.metaKey || event.ctrlKey) && event.key === "j") {
      if (this.isEditorFocused()) {
        event.preventDefault()
        this.toggleMenu()
      }
    }

    if (event.key === "Escape" && this.openValue) {
      event.preventDefault()
      this.closeMenu()
    }
  }

  isEditorFocused() {
    if (!this.editor) return false

    if (this.editorTypeValue === "lexxy") {
      return this.element.contains(document.activeElement)
    }

    return document.activeElement === this.editor
  }

  getText() {
    if (!this.editor) return ""

    switch (this.editorTypeValue) {
      case "lexxy":
        return this.getLexxyText()
      case "textarea":
      case "input":
        return this.editor.value || ""
      default:
        return ""
    }
  }

  getLexxyText() {
    if (this.editor.tagName === "LEXXY-EDITOR") {
      const text = this.editor.toString()
      if (text && !text.startsWith("[object ")) {
        return text
      }
    }

    const content = this.editor.querySelector(".lexxy-content, .trix-content, [contenteditable]")
    if (content) {
      return content.textContent || ""
    }

    return this.editor.textContent || ""
  }

  getMaxLength() {
    if (this.maxLengthValue > 0) {
      return this.maxLengthValue
    }

    if (this.editor && this.editor.maxLength > 0) {
      return this.editor.maxLength
    }

    if (this.editor && this.editor.getAttribute("maxlength")) {
      return parseInt(this.editor.getAttribute("maxlength"), 10) || 0
    }

    return 0
  }

  setText(text) {
    if (!this.editor) return

    switch (this.editorTypeValue) {
      case "lexxy":
        this.setLexxyText(text)
        break
      case "textarea":
      case "input":
        this.editor.value = text
        this.editor.dispatchEvent(new Event("input", { bubbles: true }))
        this.editor.dispatchEvent(new Event("change", { bubbles: true }))
        break
    }
  }

  setLexxyText(text) {
    const html = this.textToHtml(text)
    this.editor.value = html
    this.editor.dispatchEvent(new Event("input", { bubbles: true }))
    this.editor.dispatchEvent(new Event("change", { bubbles: true }))
  }

  textToHtml(text) {
    const escaped = text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;")

    const paragraphs = escaped.split(/\n\n+/)
    return paragraphs
      .map(p => `<p>${p.replace(/\n/g, "<br>")}</p>`)
      .join("")
  }

  toggleMenu() {
    if (this.openValue) {
      this.closeMenu()
    } else {
      this.openMenu()
    }
  }

  openMenu() {
    if (!this.hasMenuTarget) return

    this.openValue = true
    this.menuTarget.hidden = false

    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-expanded", "true")
    }

    this.createPopper()
    this.showActions()
    this.announceToScreenReader(this.menuTarget.dataset.openAnnouncement || "AI assist menu opened")

    requestAnimationFrame(() => {
      const firstButton = this.menuTarget.querySelector("button")
      if (firstButton) firstButton.focus()
    })
  }

  closeMenu() {
    if (!this.hasMenuTarget) return

    this.openValue = false
    this.menuTarget.hidden = true

    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-expanded", "false")
    }

    this.destroyPopper()
    this.resetMenuState()

    if (this.editor) {
      this.editor.focus()
    }
  }

  createPopper() {
    if (!this.hasTriggerTarget || !this.hasMenuTarget) return

    this.popperInstance = createPopper(this.triggerTarget, this.menuTarget, {
      placement: "bottom-end",
      modifiers: [
        { name: "offset", options: { offset: [0, 8] } },
        { name: "preventOverflow", options: { boundary: "viewport", padding: 8 } },
        { name: "flip", options: { fallbackPlacements: ["bottom-start", "top-end", "top-start"] } }
      ]
    })
  }

  destroyPopper() {
    if (this.popperInstance) {
      this.popperInstance.destroy()
      this.popperInstance = null
    }
  }

  async perform(event) {
    const action = event.currentTarget.dataset.aiAction
    if (!action) return

    const text = this.getText()
    if (!text.trim()) {
      this.showError(event.currentTarget.dataset.emptyError || "Text is empty")
      return
    }

    this.showLoading()

    const maxLength = this.getMaxLength()
    const payload = { text, ai_action: action }
    if (maxLength > 0) {
      payload.max_length = maxLength
    }

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        credentials: "same-origin",
        body: JSON.stringify(payload)
      })

      const contentType = response.headers.get("content-type") || ""
      if (!contentType.includes("application/json")) {
        this.showError("Server returned an unexpected response")
        return
      }

      const result = await response.json()

      if (result.success) {
        this.currentSuggestion = result.suggestion
        this.showSuggestion(result.suggestion)
        this.announceToScreenReader("Suggestion generated. Review and accept or dismiss.")
      } else {
        this.showError(result.error)
      }
    } catch (error) {
      console.error("AI Text Assist error:", error)
      this.showError("An error occurred. Please try again.")
    }
  }

  showLoading() {
    if (this.hasActionsTarget) {
      this.actionsTarget.hidden = true
    }
    if (this.hasSuggestionTarget) {
      this.suggestionTarget.hidden = true
    }
    if (this.hasLoadingTarget) {
      this.loadingTarget.hidden = false
    }
  }

  showActions() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.hidden = true
    }
    if (this.hasSuggestionTarget) {
      this.suggestionTarget.hidden = true
    }
    if (this.hasActionsTarget) {
      this.actionsTarget.hidden = false
    }
  }

  showSuggestion(text) {
    if (this.hasLoadingTarget) {
      this.loadingTarget.hidden = true
    }
    if (this.hasActionsTarget) {
      this.actionsTarget.hidden = true
    }
    if (this.hasSuggestionTarget && this.hasSuggestionTextTarget) {
      this.suggestionTextTarget.textContent = text
      this.suggestionTarget.hidden = false

      const acceptButton = this.suggestionTarget.querySelector("[data-action*='accept']")
      if (acceptButton) acceptButton.focus()
    }
  }

  showError(message) {
    this.showActions()
    this.announceToScreenReader(`Error: ${message}`)
  }

  accept() {
    if (!this.currentSuggestion) return

    this.previousValue = this.getText()
    this.setText(this.currentSuggestion)
    this.closeMenu()
    this.showUndoToast()
    this.announceToScreenReader("Suggestion accepted. Press Cmd+Z to undo.")
  }

  reject() {
    this.currentSuggestion = null
    this.showActions()
    this.announceToScreenReader("Suggestion dismissed.")
  }

  undo() {
    if (this.previousValue !== null) {
      this.setText(this.previousValue)
      this.previousValue = null
      this.hideUndoToast()
      this.announceToScreenReader("Change undone.")
    }
  }

  showUndoToast() {
    const existingToast = document.getElementById("dlnk-ai-assist-undo-toast")
    if (existingToast) existingToast.remove()

    const toast = document.createElement("div")
    toast.id = "dlnk-ai-assist-undo-toast"
    toast.className = "fixed bottom-4 right-4 z-50 flex items-center gap-3 px-4 py-3 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-lg shadow-lg animate-slide-up"
    toast.setAttribute("role", "status")

    // Build toast content safely using DOM methods (no innerHTML with user data)
    const checkSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
    checkSvg.setAttribute("class", "w-5 h-5 text-green-400 dark:text-green-600")
    checkSvg.setAttribute("fill", "none")
    checkSvg.setAttribute("viewBox", "0 0 24 24")
    checkSvg.setAttribute("stroke", "currentColor")
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
    path.setAttribute("stroke-linecap", "round")
    path.setAttribute("stroke-linejoin", "round")
    path.setAttribute("stroke-width", "2")
    path.setAttribute("d", "M5 13l4 4L19 7")
    checkSvg.appendChild(path)

    const messageSpan = document.createElement("span")
    messageSpan.className = "text-sm font-medium"
    messageSpan.textContent = this.undoMessageValue

    const undoButton = document.createElement("button")
    undoButton.type = "button"
    undoButton.className = "ml-2 px-3 py-1 text-sm font-medium bg-white/20 dark:bg-black/20 hover:bg-white/30 dark:hover:bg-black/30 rounded transition-colors"
    undoButton.textContent = this.undoActionValue
    undoButton.addEventListener("click", () => this.undo())

    toast.appendChild(checkSvg)
    toast.appendChild(messageSpan)
    toast.appendChild(undoButton)

    document.body.appendChild(toast)

    this.undoToastTimeout = setTimeout(() => {
      this.hideUndoToast()
    }, 5000)
  }

  hideUndoToast() {
    if (this.undoToastTimeout) {
      clearTimeout(this.undoToastTimeout)
      this.undoToastTimeout = null
    }

    const toast = document.getElementById("dlnk-ai-assist-undo-toast")
    if (toast) {
      toast.classList.add("animate-slide-down")
      setTimeout(() => toast.remove(), 150)
    }
  }

  resetMenuState() {
    this.currentSuggestion = null
    this.showActions()
  }

  announceToScreenReader(message) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message
    }
  }

  clickOutside(event) {
    if (this.openValue && this.hasMenuTarget && !this.menuTarget.contains(event.target) && !this.triggerTarget.contains(event.target)) {
      this.closeMenu()
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }
}
